import React from 'react';
import { Lock, Cpu } from 'lucide-react';

interface LocalModelToggleProps {
  isLocal: boolean;
  onToggle: (enabled: boolean) => void;
  language: 'uk' | 'en' | 'pl';
  className?: string;
  showDetails?: boolean;
}

export const LocalModelToggle: React.FC<LocalModelToggleProps> = ({
  isLocal,
  onToggle,
  language,
  className = '',
  showDetails = true
}) => {
  const isUk = language === 'uk';
  const isPl = language === 'pl';

  const hintText = isLocal
    ? (isPl
        ? 'Dane nie są przesyłane do chmury — przetwarzanie 100% lokalnie na serwerze (Ollama Qwen 2.5).'
        : isUk
        ? 'Дані не передаються в хмарні моделі, а обробляються 100% локально на сервері (Ollama Qwen 2.5).'
        : 'Data is processed 100% locally on-premise without cloud transfer (Ollama Qwen 2.5).')
    : (isPl
        ? 'Kliknij, aby włączyć model lokalny (dane nie będą przekazywane do chmury).'
        : isUk
        ? 'Натисніть, щоб увімкнути локальну модель (дані не передаються у хмару).'
        : 'Click to enable local model (data will not be sent to cloud).');

  const buttonActionText = isLocal
    ? (isPl ? '🔒 Model Lokalny' : isUk ? '🔒 Локальна модель' : '🔒 Local Model')
    : (isPl ? '⚡ Włącz model lokalny' : isUk ? '⚡ Увімкнути локальну модель' : '⚡ Enable Local Model');

  return (
    <div className={`flex flex-col items-end gap-0.5 ${className}`}>
      <button
        type="button"
        onClick={() => onToggle(!isLocal)}
        title={hintText}
        className={`group relative px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-2 transition-all cursor-pointer border select-none ${
          isLocal
            ? 'bg-emerald-950/70 hover:bg-emerald-900/90 text-emerald-300 border-emerald-500/50 shadow-sm shadow-emerald-950'
            : 'bg-slate-800/90 hover:bg-slate-700 text-slate-300 hover:text-white border-slate-700 hover:border-slate-600 shadow-xs'
        }`}
      >
        {isLocal ? (
          <>
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <Lock className="w-3.5 h-3.5 text-emerald-400" />
            <span className="font-bold tracking-tight">{buttonActionText}</span>
            <span className="text-[10px] px-1.5 py-0.2 bg-emerald-500/20 text-emerald-300 rounded font-mono uppercase tracking-wider font-extrabold">
              {isPl ? 'BEZ CHMURY' : isUk ? 'БЕЗ ХМАРИ' : 'OFFLINE'}
            </span>
          </>
        ) : (
          <>
            <Cpu className="w-3.5 h-3.5 text-slate-400 group-hover:text-amber-400 transition-colors" />
            <span>{buttonActionText}</span>
          </>
        )}
      </button>

      {showDetails && (
        <span className="text-[10px] text-right max-w-xs leading-tight font-sans">
          {isLocal ? (
            <span className="text-emerald-400/90 font-medium">
              ✓ {isPl ? 'Dane nie trafiają do chmury (lokalnie)' : isUk ? 'Дані не передаються у хмару (обробка локально)' : 'Data stays on-premise (local)'}
            </span>
          ) : (
            <span 
              className="text-slate-400 hover:text-slate-300 cursor-pointer underline decoration-dotted" 
              onClick={() => onToggle(true)}
            >
              {isPl ? 'Włącz przetwarzanie bez chmury' : isUk ? 'Вимкнено передачу в хмару (клікніть)' : 'Enable cloud-free execution'}
            </span>
          )}
        </span>
      )}
    </div>
  );
};

export default LocalModelToggle;
